import { useState } from 'react';
import { useRouter } from 'next/router';
import ReCAPTCHA from 'react-google-recaptcha';
import { v4 as uuid } from 'uuid';
import { z } from 'zod';

import { Box, Flex, Heading, Stack, Text } from '@chakra-ui/react';

import { Alert, AlertMessage } from '~/components/alert';
import { Glyph } from '~/components/glyph';
import { LoadingOverlay } from '~/components/loading-overlay';
import { TemplateForm } from '~/components/template-form';
import { useLeadFormEvent } from '~/features/analytics/hooks/use-lead-form-event';
import { postPersonalizedDiscountForm } from '~/features/discount/controllers/post-personalized-discount-form';
import { addCouponCookie } from '~/features/discount/utils/add-coupon-cookie';
import { publicEnv } from '~/features/environment/public';
import { TemplateForm as TemplateFormType } from '~/features/page-template/models/template-form';
import { phoneNumberSchema } from '~/features/phone-number/models';
import { postSignup } from '~/features/quick-authentication/controllers/post-signup';
import { QuickAuthenticationError } from '~/features/quick-authentication/errors';
import { useUser } from '~/features/user/hooks/use-user';
import { transformFormDataToEventProperties } from '~/utils/transform-form-data-to-event-properties';

const formData = [
  {
    id: '1',
    order: 0,
    required: true,
    label: 'What is your employment status?',
    inputType: 'select',
    selectOptions: 'Full Time | Part Time or Contract | Self Employed | Unemployed | Student | Other',
    placeholder: 'Select an option',
    name: 'employmentStatus',
    conditional: '',
  },
  {
    id: '2',
    order: 1,
    required: true,
    name: 'income',
    label: 'What was your total personal income for the most recent reporting period?',
    inputType: 'select',
    selectOptions:
      '$0 - $11,999 | $12,000 - $24,999 | $25,000 - $49,999 | $50,000 - $74,999 | $75,000 - $99,999 | $100,000 - $199,999 | $200,000+ | Prefer not to disclose',
    placeholder: 'Select an option',
  },
  {
    id: '3',
    order: 2,
    required: true,
    label: 'What is your email address?',
    inputType: 'email',
    placeholder: '',
    name: 'email',
  },
  {
    id: '4',
    order: 3,
    required: false,
    name: 'textOffer',
    label: 'Text me my offer (optional)',
    inputType: 'checkbox',
    conditional: '',
  },
  {
    id: '5',
    order: 4,
    required: false,
    name: 'phoneNumber',
    label: 'What is your phone number?',
    inputType: 'tel',
    conditional: 'textOffer',
    placeholder: '',
  },
] as const;

const formTemplate: TemplateFormType = {
  _id: uuid(),
  _type: 'form',
  formName: 'Personalized Offer',
  formData: JSON.stringify(formData),
  submitButtonText: 'Get Offer',
  cancelButtonText: null,
  submitButton: null,
  cancelButton: null,
  sendToSalesforce: false,
  legalText: null,
} as const;

const personalizedDiscountFormSchema = z.object({
  employmentStatus: z.object({
    label: z.string(),
    value: z.string(),
  }),
  income: z.object({
    label: z.string(),
    value: z.string(),
  }),
  email: z.string().email(),
  textOffer: z.boolean(),
  phoneNumber: phoneNumberSchema.optional(),
});

export function PersonalizedDiscountForm() {
  const router = useRouter();
  const { user, userId } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<AlertMessage | undefined>();
  const [isCaptchaRequired, setIsCaptchaRequired] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string>();
  const { isSubmitting, sendSegmentLeadFormEvents } = useLeadFormEvent();

  async function handlePersonalizedDiscountForm({
    formData,
  }: {
    templateForm: TemplateFormType;
    formData: Record<string, string | { label: string; value: string }>;
  }) {
    setIsLoading(true);
    setFormError(undefined);
    setIsCaptchaRequired(false);

    const personalizedDiscountFormData = personalizedDiscountFormSchema.parse(formData);

    // If user isn't authenticated then we go through the quick auth flow
    if (!userId) {
      try {
        await postSignup(personalizedDiscountFormData.email, captchaValue);
      } catch (error) {
        if (error instanceof QuickAuthenticationError) {
          switch (error.name) {
            // User already has an account, take them to login and redirect back to this page
            case 'EMAIL_EXISTS':
              window.location.href = `${publicEnv.NEXT_PUBLIC_AUTH_URL}/sign-in?next=${window.location.href}`;
              setFormError({
                message: 'An account with this email already exists, please login to continue.',
                level: 'error',
              });
              break;
            case 'CAPTCHA_REQUIRED':
              setFormError({ message: error.message, level: 'warning' });
              setIsCaptchaRequired(true);
              break;
            default:
              setFormError({ message: 'An error occurred signing up. Please try again.', level: 'error' });
          }
        }
        setIsLoading(false);
      }
    }
    try {
      const couponResponse = await postPersonalizedDiscountForm(personalizedDiscountFormData);

      addCouponCookie(couponResponse.couponCode, {
        expires: new Date(couponResponse.expiresAt),
      });

      const params = new URLSearchParams({
        couponCode: encodeURIComponent(couponResponse.couponCode),
        percentDiscount: encodeURIComponent(couponResponse.percentDiscount),
        expiresAt: encodeURIComponent(couponResponse.expiresAt),
      }).toString();

      await sendSegmentLeadFormEvents({
        identifyTraits: {
          email: formData.email,
          phone: formData.phoneNumber,
        },
        trackAnalyticsEvent: {
          name: 'Marketing Qualified Lead Form Submitted',
          type: 'submit',
          label: 'Personalized Discount',
          action: 'form_submitted',
          additionalAttributes: transformFormDataToEventProperties(formData),
        },
        integrations: {
          sendToSalesforce: true,
        },
      });

      router.push(`/personalized-discount-success?${params}`);
    } catch (error) {
      setFormError({ message: 'An error occurred signing up. Please try again.', level: 'error' });
    }
  }

  return (
    <Stack spacing={'40px'}>
      <Flex>
        <Stack spacing={'8px'}>
          <Heading
            as='h2'
            color='blue.400'
            size={{
              base: 'marketing-lg',
              md: 'marketing-2xl',
            }}
          >
            Get Your Personalized Offer
          </Heading>
          <Text>Answer just three questions and save!</Text>
        </Stack>

        <Box
          marginLeft='auto'
          display={{
            base: 'none',
            md: 'block',
          }}
        >
          <Glyph height={56} name={'sale-tag'} width={56} />
        </Box>
      </Flex>

      {formError && <Alert status={formError.level}>{formError.message}</Alert>}

      {isCaptchaRequired && setCaptchaValue && (
        <>
          <ReCAPTCHA
            sitekey={publicEnv.NEXT_PUBLIC_USER_API_RECAPTCHA_KEY}
            size='normal'
            onChange={(value) => {
              if (value) {
                setCaptchaValue(value);
              }
            }}
          />
        </>
      )}
      <Box position='relative'>
        {isLoading && <LoadingOverlay message='Creating your offer...' />}

        <TemplateForm
          key={user?.email}
          defaultValues={{ email: user?.email || '' }}
          isFullWidth={true}
          templateForm={formTemplate}
          submitButtonProps={{
            isLoading: isSubmitting,
            marginX: 'auto',
          }}
          onSubmit={handlePersonalizedDiscountForm}
        />
      </Box>
    </Stack>
  );
}
